import {
  ArrayType,
  ContextEntry,
  Errors,
  IntersectionType,
  LiteralType,
  UnionType,
  ValidationError
} from "io-ts";
import { _Error } from "@/lib/wordpress/data";
import { isPlainObject, truncate } from "lodash";

class ErrorTree {
  public children: Record<string, ErrorTree> = {};
  public error: ValidationError | null = null;

  constructor(
    public readonly parent: ErrorTree | null = null,
    public context: ContextEntry | null = null
  ) {}

  getChildren() {
    return Object.values(this.children);
  }

  addError(error: ValidationError) {
    const ctx: ContextEntry[] = [...error.context];
    this.context = ctx.shift() ?? null;
    this.addErrorInternal(ctx, error);
  }

  addErrorInternal(ctx: ContextEntry[], error: ValidationError) {
    const currContext = ctx.shift();
    if (currContext) {
      const key = currContext.key;

      if (!this.children[key]) {
        this.children[key] = new ErrorTree(this, currContext);
      }

      this.children[key].addErrorInternal(ctx, error);
    } else {
      this.error = error;
    }
  }
}

function fromErrors(errors: ValidationError[]) {
  const tree = new ErrorTree();
  errors.forEach(error => {
    tree.addError(error);
  });
  return tree;
}

function stringify(v: unknown) {
  if (typeof v === "function") {
    return getFunctionName(v);
  }

  if (typeof v === "number" && !isFinite(v)) {
    if (isNaN(v)) {
      return "NaN";
    }
    return v > 0 ? "Infinity" : "-Infinity";
  }

  // eslint-disable-next-line no-restricted-properties
  return JSON.stringify(v);
}

const isPlainObjectTypeSafe = (
  v: unknown
): v is Record<string | symbol, unknown> => isPlainObject(v);

type VisitorFnOutput = { includeChildren: boolean; consoleArgs: any[] };

function printExplanation(
  tree: ErrorTree,
  visitorFn: (t: ErrorTree) => VisitorFnOutput
): void {
  const { consoleArgs, includeChildren } = visitorFn(tree);
  const children = includeChildren ? tree.getChildren() : [];

  if (includeChildren && children.length > 0) {
    console.group(...consoleArgs);
    children.map(child => printExplanation(child, visitorFn));
    console.groupEnd();
  } else {
    console.log(...consoleArgs);
  }
}

const tr = (str: string, length = 30) => truncate(str, { length });

const getSelfWithParents = (tree: ErrorTree): ErrorTree[] => [
  tree,
  ...(tree.parent ? getSelfWithParents(tree.parent) : [])
];

const getFunctionName = (f: Function | (() => unknown)) =>
  f.name || `<function${f.length}>`;

const isLiteralUnion = (tree: ErrorTree): boolean =>
  tree.context?.type instanceof UnionType &&
  tree.getChildren().every(child => child.context?.type instanceof LiteralType);

const getPath = (tree: ErrorTree): string | undefined => {
  const path = getSelfWithParents(tree)
    .reverse()
    .filter(t =>
      t.parent
        ? !(
            t.parent?.context?.type instanceof UnionType ||
            t.parent?.context?.type instanceof IntersectionType
          )
        : !(
            t.context?.type instanceof UnionType ||
            t.context?.type instanceof IntersectionType
          )
    )
    .map(t =>
      t.parent?.context?.type instanceof ArrayType
        ? `[${t.context?.key}]`
        : t.context?.key
    )
    .join(".");

  return path.length ? path : undefined;
};

const missingPropertyVisitor = (tree: ErrorTree): VisitorFnOutput => {
  const actualValue = tree.context?.actual;
  const type = tree.context?.type;
  const typeName = tree.context?.type.name ?? "UNKNOWN";
  const key = tree.context?.key;
  const message = tree.error && tree.error.message;

  const parent = tree.parent;
  const parentActualValue = parent && parent.context?.actual;
  const parentType = parent?.context?.type;
  const parentTypeName = (parent && parent.context?.type.name) ?? "UNKNOWN";
  const isMissingProperty =
    actualValue === undefined &&
    key &&
    parent &&
    isPlainObjectTypeSafe(parentActualValue) &&
    !(key in parentActualValue);

  const includeChildren = !isLiteralUnion(tree);
  const consoleArgs = [];

  const path = getPath(tree);

  if (message) {
    consoleArgs.push(message);
  } else if (isMissingProperty) {
    consoleArgs.push(
      "Property `%s` is missing in type `%s` (%O), but is required in type `%s` (%O)",
      path,
      tr(stringify(parentActualValue)),
      parentActualValue,
      tr(parentTypeName),
      parentType
    );
  } else {
    if (isPlainObjectTypeSafe(actualValue) || Array.isArray(actualValue)) {
      consoleArgs.push(
        "Type `%s` (%O)%s is not assignable to type `%s` (%O)",
        tr(stringify(actualValue)),
        actualValue,
        path ? ` at path \`${path}\`` : "",
        tr(typeName),
        type
      );
    } else {
      consoleArgs.push(
        "Type `%s`%s is not assignable to type `%s` (%O)",
        tr(stringify(actualValue)),
        path ? ` at path \`${path}\`` : "",
        tr(typeName),
        type
      );
    }
  }

  return {
    includeChildren,
    consoleArgs
  };
};

export function iotsErrorsToCustomErrorType(errors: Errors): _Error[] {
  console.trace("IO-TS validation failed, see the details below");
  printExplanation(fromErrors(errors), missingPropertyVisitor);

  return [
    {
      msg: "IO-TS decoding failed",
      customNotificationToShow: {
        type: "IO_TS",
        errorMessage: "IO-TS decoding failed"
      }
    }
  ];
}
